import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ActionLink } from 'components/shared';
import { get, find } from 'lodash';
import * as immutable from 'object-path-immutable';
import CCEvents, { EventNames } from '@companycam/companycam-events';

import RearrangeModalHeader from './RearrangeModalHeader';
import Wizard from '../../core/forms/Wizard';
import toast from 'helpers/toast';
import SelectPhotos from './steps/SelectPhotos';
import SelectSection from './steps/selectSection/SelectSection';
import {
  GET_REPORT,
  CREATE_REPORT_NODE,
  UPDATE_REPORT_NODE_POSITION_BULK,
  UPDATE_REPORT_NODE_TREE_POSITION_BULK,
} from '../gql/reports.graphql';
import { translate } from 'translations/i18n';

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 32px;
`;

const HeaderWrapper = styled.div`
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--cc_color_border_default);
  font-weight: 600;
`;

const RearrangeModal = ({
  onRequestClose,
  entries,
  isMovingSection,
  sections,
  currentSection,
  sectionNodes,
  reportId,
  handleSectionSelect,
  reportSettings,
  refetchReport,
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState(currentSection);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('Default Title');
  const [newSectionSummary, setNewSectionSummary] = useState('');

  // Remove currently selected items out of the displayed items in the section
  useEffect(() => {
    const sectionEntries = sectionNodes.filter(
      (sectionNode) => sectionNode.parent.id === selectedSection.id,
    );

    const selectedIds = selectedItems.map((item) => {
      return item.id;
    });

    const toRemove = new Set(selectedIds);
    const entriesWithoutSelections = sectionEntries.filter(
      (item) => !toRemove.has(item.id),
    );

    setFilteredEntries(entriesWithoutSelections);
  }, [selectedItems, sectionNodes, selectedSection]);

  const [updatePosition] = useMutation(UPDATE_REPORT_NODE_POSITION_BULK, {
    onError: () => {
      toast.error('There was a problem updating the report');
    },

    onCompleted: () => {
      CCEvents.logEvent(EventNames.REPORTS.SINGLE_REARRANGE);
      onRequestClose();
    },
  });

  const [moveToSection] = useMutation(UPDATE_REPORT_NODE_TREE_POSITION_BULK, {
    onCompleted: () => {
      handleSectionSelect({
        id: selectedSection.id,
        type: 'ASSET_SECTION',
      });
      refetchReport();
      onRequestClose();
    },
  });

  const rearrangeSubmitAction = (nodesWithPositions) => {
    updatePosition({
      variables: {
        input: {
          nodesWithPositions,
        },
      },
    });
  };

  const handleSingleSave = (values) => {
    const { photos } = values;
    const nodesWithPositions = photos.map((item, index) => {
      return {
        id: item.id,
        position: index + 1,
      };
    });

    rearrangeSubmitAction(nodesWithPositions);
  };

  const handleMultiSelect = (photo) => {
    const itemInArray = find(selectedItems, photo);

    if (itemInArray === undefined) {
      setSelectedItems((arr) => [...arr, photo]);
    } else {
      // item is already selected, remove it
      const updatedArray = selectedItems.filter((item) => {
        return item.id !== photo.id;
      });
      setSelectedItems(updatedArray);
    }
  };

  const handleMoveItems = async (newSectionId) => {
    const selectedId = newSectionId || selectedSection.id;
    const position = selectedPosition || 1;
    const beforeInsert = filteredEntries.slice(0, position);
    const afterInsert = filteredEntries.slice(position);

    const updatedSectionNodes = newSectionId
      ? [...selectedItems]
      : [...beforeInsert, ...selectedItems, ...afterInsert];
    const nodesWithPositions = updatedSectionNodes.map((node, index) => ({
      id: node.id,
      position: index + 1,
    }));
    try {
      await moveToSection({
        variables: {
          input: {
            nodesWithPositions,
            reportId,
            parentId: selectedId,
          },
        },
      });

      if (selectedId === currentSection.id) {
        CCEvents.logEvent(EventNames.REPORTS.BULK_REARRANGE);
      } else {
        CCEvents.logEvent(EventNames.REPORTS.BULK_REARRANGE_NEW_SECTION);
      }
    } catch (error) {
      toast.error('There was a problem updating the report');
    }
  };

  const [createSection] = useMutation(CREATE_REPORT_NODE, {
    onError: () => {
      toast.error('There was a problem creating the section');
    },
    update: (
      cache,
      {
        data: {
          reportNodeCreate: { reportNode },
        },
      },
    ) => {
      const cachedData = cache.readQuery({
        query: GET_REPORT,
        variables: { id: reportId },
      });

      const updatedReportNodes = [
        ...get(cachedData, 'viewer.report.reportNodes.nodes', []),
        reportNode,
      ];

      cache.writeQuery({
        query: GET_REPORT,
        data: immutable.set(cachedData, 'viewer.report.reportNodes.nodes', [
          ...updatedReportNodes,
        ]),
      });
    },
  });

  const handleCreateSection = async () => {
    await createSection({
      variables: {
        input: {
          type: 'ASSET_SECTION',
          name: newSectionName,
          position: sections.length + 1,
          reportId,
          data: {
            assetSection: {
              summary: newSectionSummary,
            },
          },
        },
      },
    }).then((result) => {
      const newSection = get(
        result,
        'data.reportNodeCreate.reportNode' || null,
      );
      if (newSection) {
        handleMoveItems(newSection.id);
        setSelectedSection(newSection);
      }
    });
  };

  const handleCreateSectionAndMoveItems = handleCreateSection;

  const handleMoveSubmit = () => {
    if (!creatingSection) {
      handleMoveItems();
    } else {
      handleCreateSectionAndMoveItems();
    }
  };

  const sectionSelection = (selection) => {
    setSelectedSection(selection);
    setSelectedPosition(null);
  };

  const initialValues = {
    photos: entries,
  };

  const saveAction = isMovingSection ? handleMoveSubmit : handleSingleSave;

  const progressBarLabels = [
    isMovingSection
      ? translate('photos.reassignPhotosModal.title')
      : translate('reports.rearrange.label.selectPhotos'),
    translate('reports.rearrange.label.selectSection'),
  ];

  return (
    <Modal
      ariaHideApp={false}
      contentLabel="Move Photos"
      isOpen
      overlayClassName="modal"
      className="modal-content-wrap rearrange-wizard is-padless"
    >
      <ModalContentContainer>
        <HeaderWrapper>
          <RearrangeModalHeader
            title={isMovingSection ? 'Move Photos' : 'Rearrange Photos'}
          />
          <ActionLink className="modal-close-button" onClick={onRequestClose}>
            <i size={40} className="mdi mdi-close" />
          </ActionLink>
        </HeaderWrapper>
        {/* Begin Wizard */}
        <Wizard
          onSubmit={(values) => saveAction(values)}
          initialValues={initialValues}
          submitStep={
            isMovingSection
              ? translate('reports.rearrange.label.confirmSelection')
              : translate('reports.rearrange.saveButton')
          }
          progressBarLabels={progressBarLabels}
          selectedItems={selectedItems}
          hideSelectedCount={!isMovingSection}
        >
          <SelectPhotos
            heading={isMovingSection ? 'Move Photos' : 'Rearrange Photos'}
            description={
              isMovingSection
                ? 'Select the photos you want to move'
                : 'Click and drag to rearrange'
            }
            entries={entries}
            isMovingSection={isMovingSection}
            handleMultiSelect={handleMultiSelect}
            selectedItems={selectedItems}
            nextStepName={translate('core.forms.wizard.nextStepButton')}
          />
          {isMovingSection ? (
            <SelectSection
              heading={`Add ${selectedItems.length} photos to ${selectedSection.name}`}
              description="Choose the section you want to add your photos to. You can select where to add them in-line."
              sections={sections}
              selectedSection={selectedSection}
              handleSectionSelect={sectionSelection}
              currentSection={currentSection}
              sectionNodes={filteredEntries}
              setSelectedPosition={setSelectedPosition}
              selectedPosition={selectedPosition}
              creatingSection={creatingSection}
              setCreatingSection={setCreatingSection}
              setNewSectionName={setNewSectionName}
              setNewSectionSummary={setNewSectionSummary}
              newSectionName={newSectionName}
              newSectionSummary={newSectionSummary}
              reportSettings={reportSettings}
            />
          ) : null}
        </Wizard>
      </ModalContentContainer>
    </Modal>
  );
};

RearrangeModal.propTypes = {
  onRequestClose: PropTypes.func,
  isMovingSection: PropTypes.bool,
  entries: PropTypes.array,
  sections: PropTypes.array,
  currentSection: PropTypes.object,
  sectionNodes: PropTypes.array,
  reportId: PropTypes.string,
  handleSectionSelect: PropTypes.func,
  reportSettings: PropTypes.object,
  refetchReport: PropTypes.func,
};

export default RearrangeModal;
